import { createSlice } from '@reduxjs/toolkit';
import { resetAll } from '../../../shared/infra/redux/baseActions';
import { ReduxStatus } from '../../../shared/types';
import { Profile } from '../models/profile';
import {
  verifyUserEmailToken,
  verifyResetToken,
  requestPasswordReset,
  resetPassword,
  getCommunityProfile,
} from './operators';
import verifyUserDeletionToken from './operators/verifyUserDeletionToken';

export interface PublicState {
  isEmailVerified?: boolean;
  resetTokenIsValid?: boolean;
  isDeletionVerified?: boolean;
  profile: Profile | null;
  isEmailVerifiedStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  passwordResetRequestStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  requestResetStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  resetUserPasswordStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getCommunityProfileStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  verifyUserDeletionStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  requestDeleteUserStatus: {
    status: ReduxStatus;
    error: string | null;
  };
}

const initialState: PublicState = {
  isEmailVerified: undefined,
  isDeletionVerified: undefined,
  resetTokenIsValid: undefined,
  profile: null,
  isEmailVerifiedStatus: {
    status: 'idle',
    error: null,
  },
  passwordResetRequestStatus: {
    status: 'idle',
    error: null,
  },
  requestResetStatus: {
    status: 'idle',
    error: null,
  },
  resetUserPasswordStatus: {
    status: 'idle',
    error: null,
  },
  getCommunityProfileStatus: {
    status: 'idle',
    error: null,
  },
  verifyUserDeletionStatus: {
    status: 'idle',
    error: null,
  },
  requestDeleteUserStatus: {
    status: 'idle',
    error: null,
  },
};

const publicSlice = createSlice({
  name: 'public',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(verifyUserEmailToken.pending, (state) => {
      state.isEmailVerifiedStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(verifyUserEmailToken.fulfilled, (state) => {
      state.isEmailVerifiedStatus = {
        status: 'success',
        error: null,
      };
      state.isEmailVerified = true;
    });
    builder.addCase(verifyUserEmailToken.rejected, (state, { payload }) => {
      state.isEmailVerifiedStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to verify user',
      };
      state.isEmailVerified = false;
    });
    builder.addCase(verifyResetToken.pending, (state) => {
      state.passwordResetRequestStatus = {
        status: 'loading',
        error: null,
      };
      state.resetTokenIsValid = undefined;
    });
    builder.addCase(verifyResetToken.fulfilled, (state) => {
      state.passwordResetRequestStatus = {
        status: 'success',
        error: null,
      };
      state.resetTokenIsValid = true;
    });
    builder.addCase(verifyResetToken.rejected, (state, { payload }) => {
      state.passwordResetRequestStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to verify reset token',
      };
      state.resetTokenIsValid = false;
    });
    builder.addCase(requestPasswordReset.pending, (state) => {
      state.requestResetStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(requestPasswordReset.fulfilled, (state) => {
      state.requestResetStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(requestPasswordReset.rejected, (state, { payload }) => {
      state.requestResetStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error during password reset request',
      };
    });
    builder.addCase(resetPassword.pending, (state) => {
      state.resetUserPasswordStatus = {
        status: 'idle',
        error: null,
      };
    });
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.resetUserPasswordStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(resetPassword.rejected, (state, { payload }) => {
      state.resetUserPasswordStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error resetting user password',
      };
    });
    builder.addCase(getCommunityProfile.pending, (state) => {
      state.getCommunityProfileStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getCommunityProfile.fulfilled, (state, { payload }) => {
      state.getCommunityProfileStatus = {
        status: 'success',
        error: null,
      };
      state.profile = payload;
    });
    builder.addCase(getCommunityProfile.rejected, (state, { payload }) => {
      state.getCommunityProfileStatus = {
        status: 'failure',
        error: payload ? payload.message : 'Could not fetch community profile',
      };
    });
    builder.addCase(verifyUserDeletionToken.pending, (state) => {
      state.verifyUserDeletionStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(verifyUserDeletionToken.fulfilled, (state) => {
      state.verifyUserDeletionStatus = {
        status: 'success',
        error: null,
      };
      state.isDeletionVerified = true;
    });
    builder.addCase(verifyUserDeletionToken.rejected, (state, { payload }) => {
      state.verifyUserDeletionStatus = {
        status: 'failure',
        error: payload
          ? payload.message
          : 'Error trying to verify user deletion',
      };
      state.isDeletionVerified = false;
    });
    builder.addCase(resetAll, () => initialState);
  },
});

export default publicSlice.reducer;
